import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Compass,
  Boxes,
  Activity,
  UserCheck,
  ShieldCheck,
  Settings,
  HelpCircle,
  Search,
  Bell,
  ChevronDown,
  Check,
  LogOut,
  Keyboard as KeyboardIcon,
  Sun,
  Moon,
} from "lucide-react";
import Logo from "@/components/aieco/Logo";
import CommandPalette from "@/components/aieco/CommandPalette";
import LiveDemoMode from "@/components/aieco/LiveDemoMode";
import ShortcutsModal from "@/components/aieco/ShortcutsModal";
import LanguageSelector from "@/components/aieco/LanguageSelector";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { WORKSPACE_LIST } from "@/data/workspaces";
import useKeyboardShortcuts from "@/hooks/useKeyboardShortcuts";

const NAV = [
  { to: "/", label: "Command Center", icon: LayoutDashboard, end: true },
  { to: "/discovery", label: "AI Discovery", icon: Compass },
  { to: "/inventory", label: "AI Inventory", icon: Boxes },
  { to: "/runtime", label: "Runtime Monitor", icon: Activity, hot: true },
  { to: "/oversight", label: "Human Oversight", icon: UserCheck, count: 2 },
  { to: "/audit", label: "Audit Center", icon: ShieldCheck },
];

function WorkspaceSwitcher() {
  const { workspaceId, setWorkspaceId, data } = useWorkspace();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" data-testid="workspace-switcher">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-white/5 bg-white/[0.02] p-3 text-left hover:border-white/10"
      >
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Workspace</div>
          <div className="mt-1 text-[13px] font-medium text-slate-100">{data.name}</div>
          <div className="text-[11px] text-slate-500">{data.description}</div>
        </div>
        <ChevronDown size={14} className={`text-slate-500 transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 z-40 mt-1 rounded-lg border border-white/10 bg-[#0f1730] p-1 shadow-2xl">
          {WORKSPACE_LIST.map((w) => (
            <button
              key={w.id}
              onClick={() => {
                setWorkspaceId(w.id);
                setOpen(false);
              }}
              data-testid={`workspace-option-${w.id}`}
              className={`flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-[12px] hover:bg-white/[0.04] ${
                workspaceId === w.id ? "text-white" : "text-slate-300"
              }`}
            >
              <div>
                <div className="font-medium">{w.name}</div>
                <div className="text-[11px] text-slate-500">{w.description}</div>
              </div>
              {workspaceId === w.id && <Check size={13} className="text-[#5B8DEF]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function UserBadge() {
  const { user, login, logout } = useAuth();
  const [open, setOpen] = useState(false);
  if (!user) {
    return (
      <button
        onClick={login}
        data-testid="sign-in-google"
        className="flex items-center gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-2.5 py-1.5 text-[12px] text-slate-200 hover:border-white/10"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0012 23z" opacity=".8"/>
          <path fill="#fff" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A10.97 10.97 0 001 12c0 1.78.42 3.46 1.18 4.94l3.66-2.84z" opacity=".6"/>
          <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" opacity=".4"/>
        </svg>
        Sign in with Google
      </button>
    );
  }
  return (
    <div className="relative" data-testid="user-menu">
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-2 py-1 hover:border-white/10">
        {user.picture ? (
          <img src={user.picture} alt={user.name} className="h-6 w-6 rounded-full" />
        ) : (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-[#5B8DEF] to-[#22C55E] text-[10px] font-semibold text-[#0b1020]">
            {(user.name || user.email).slice(0, 2).toUpperCase()}
          </div>
        )}
        <span className="hidden text-[12px] text-slate-200 sm:inline" data-testid="user-name">{user.name}</span>
        <ChevronDown size={12} className="text-slate-500" />
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-1 w-56 rounded-lg border border-white/10 bg-[#0f1730] p-1 shadow-2xl">
          <div className="border-b border-white/5 px-3 py-2">
            <div className="text-[12px] text-slate-100">{user.name}</div>
            <div className="text-[11px] text-slate-500">{user.email}</div>
          </div>
          <button
            onClick={logout}
            data-testid="sign-out"
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-[12px] text-slate-300 hover:bg-white/[0.04]"
          >
            <LogOut size={12} /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function TopBar({ onOpenPalette, onOpenShortcuts }) {
  const loc = useLocation();
  const { theme, toggle } = useTheme();
  const current =
    NAV.find((n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to))) ||
    (loc.pathname.startsWith("/settings")
      ? { label: "Settings · SSO & SCIM" }
      : loc.pathname.startsWith("/explore")
        ? { label: "Metric Explorer" }
        : { label: "AI-ECO" });
  return (
    <header
      className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-white/5 bg-[#0b1020]/80 px-6 backdrop-blur-xl"
      data-testid="topbar"
    >
      <div className="flex items-center gap-3 text-sm">
        <span className="text-slate-500">AI-ECO</span>
        <span className="text-slate-700">/</span>
        <span className="text-slate-200">{current.label}</span>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={onOpenPalette}
          data-testid="open-palette"
          className="hidden items-center gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-1.5 text-[12px] text-slate-400 hover:border-white/10 md:flex"
        >
          <Search size={13} strokeWidth={1.6} />
          <span>Search agents, policies, events…</span>
          <span className="ml-3 rounded border border-white/5 bg-white/[0.03] px-1.5 py-0.5 font-mono text-[10px] text-slate-500">⌘K</span>
        </button>
        <button
          onClick={onOpenShortcuts}
          data-testid="open-shortcuts"
          title="Keyboard shortcuts"
          className="rounded-lg border border-white/5 bg-white/[0.02] p-1.5 text-slate-300 hover:border-white/10"
        >
          <KeyboardIcon size={14} strokeWidth={1.6} />
        </button>
        <button
          onClick={toggle}
          data-testid="theme-toggle"
          title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
          className="rounded-lg border border-white/5 bg-white/[0.02] p-1.5 text-slate-300 hover:border-white/10"
        >
          {theme === "dark" ? <Sun size={14} strokeWidth={1.6} /> : <Moon size={14} strokeWidth={1.6} />}
        </button>
        <LiveDemoMode />
        <LanguageSelector />
        <button className="flex items-center gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-2.5 py-1.5 text-[12px] text-slate-300 hover:border-white/10" data-testid="env-switcher">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
          Production
          <ChevronDown size={12} strokeWidth={1.6} />
        </button>
        <button className="rounded-lg border border-white/5 bg-white/[0.02] p-1.5 text-slate-300 hover:border-white/10" data-testid="notifications-btn">
          <Bell size={14} strokeWidth={1.6} />
        </button>
        <UserBadge />
      </div>
    </header>
  );
}

function Sidebar() {
  const { data } = useWorkspace();
  return (
    <aside
      className="hidden w-64 shrink-0 flex-col border-r border-white/5 bg-[#111827] lg:flex"
      data-testid="sidebar"
    >
      <div className="px-5 py-4">
        <Logo />
      </div>

      <div className="mx-4 mb-3">
        <WorkspaceSwitcher />
      </div>

      <nav className="flex-1 px-3">
        <div className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          Platform
        </div>
        <ul className="space-y-0.5">
          {NAV.map((n) => (
            <li key={n.to}>
              <NavLink
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `group flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-[13px] transition-colors ${
                    isActive
                      ? "bg-white/[0.04] text-white"
                      : "text-slate-400 hover:bg-white/[0.02] hover:text-slate-100"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="flex items-center gap-2.5">
                      <n.icon size={15} strokeWidth={1.6} className={isActive ? "text-[#5B8DEF]" : ""} />
                      {n.label}
                    </span>
                    {n.count ? (
                      <span className="rounded-full bg-[#5B8DEF]/15 px-1.5 py-0.5 font-mono text-[10px] text-[#5B8DEF]">
                        {n.count}
                      </span>
                    ) : n.hot ? (
                      <span className="rounded-full bg-emerald-400/15 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-emerald-300">
                        live
                      </span>
                    ) : null}
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="px-2 pb-2 pt-5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          System
        </div>
        <ul className="space-y-0.5">
          <li>
            <NavLink
              to="/settings"
              data-testid="nav-settings"
              className={({ isActive }) =>
                `group flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] transition-colors ${
                  isActive ? "bg-white/[0.04] text-white" : "text-slate-400 hover:bg-white/[0.02] hover:text-slate-100"
                }`
              }
            >
              <Settings size={15} strokeWidth={1.6} />
              Settings · SSO & SCIM
            </NavLink>
          </li>
          <li>
            <button className="group flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] text-slate-400 hover:bg-white/[0.02] hover:text-slate-100">
              <HelpCircle size={15} strokeWidth={1.6} />
              Docs
            </button>
          </li>
        </ul>
      </nav>

      <div className="m-4 rounded-lg border border-white/5 bg-gradient-to-br from-[#5B8DEF]/10 to-[#22C55E]/[0.04] p-3" data-testid="agri-mini">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">AGRI™ Status</div>
        <div className="mt-1 flex items-baseline gap-1.5">
          <span className="font-mono text-2xl font-light text-white">{data.agriScore}</span>
          <span className="text-[11px] text-slate-500">/ 100</span>
        </div>
        <div className="text-[11px] text-emerald-300">
          {data.agriScore >= 80 ? "Managed" : data.agriScore >= 70 ? "Controlled" : "Attention required"}
        </div>
      </div>
    </aside>
  );
}

export default function AppShell({ children }) {
  const [palette, setPalette] = useState(false);
  const [shortcuts, setShortcuts] = useState(false);

  useKeyboardShortcuts({
    onHelp: () => setShortcuts(true),
  });

  return (
    <div className="flex min-h-screen bg-[#0B1020] text-white">
      <a href="#main-content" className="skip-link" data-testid="skip-to-content">
        Skip to main content
      </a>
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onOpenPalette={() => setPalette(true)} onOpenShortcuts={() => setShortcuts(true)} />
        <main id="main-content" tabIndex={-1} className="bg-grid bg-radial-fade min-h-[calc(100vh-3.5rem)]">{children}</main>
      </div>
      <CommandPalette open={palette} onOpenChange={setPalette} />
      <ShortcutsModal open={shortcuts} onClose={() => setShortcuts(false)} />
    </div>
  );
}
